/**
 * galaxy-overlays.ts — v4M6.2 strategic overlay layer.
 *
 * Four mutually-exclusive read-only overlays per `docs/MAP.md §3.3`:
 *
 *   - Production    — viewer-empire per-tick income heat-map. Reads
 *                     "where am I making money?"
 *   - Threat        — per-system enemy-strength roll-up via v4M8.2a
 *                     `enemyStrength`. Reads "where am I exposed?"
 *   - Exploration   — per-system scouted / unscouted binary. Reads
 *                     "what's left to explore?"
 *   - Diplomatic    — per-system dominant diplomatic state (own /
 *                     federated / vassal-host / trading / courting /
 *                     at-war / neutral / unknown). Reads "who do I
 *                     have what relationship with?"
 *
 * Overlay state lives in `UiState.galaxyOverlay` (not on the save
 * format). Persisted to localStorage via `loadGalaxyOverlay` /
 * `saveGalaxyOverlay` so the choice survives a page reload — the same
 * convention used by the M14 placement modes (UI-pref-style storage).
 *
 * Rendered between the star halo and the per-system indicator stack
 * in galaxy-view.ts. Soft per-system tinted ring around each star, no
 * alpha overlap with the existing halo / corona.
 */

import { areTeammates } from '../game/teams';
import { enemyStrength } from '../game/advance-ai-targeting';
import { habitabilityFor, popDeltaPerTick, productionEfficiencyMultiplier } from '../game/balance-habitability';
import { economyMultipliersForPlanet } from '../game/planet-economy';
import {
  EMPIRE_NEUTRAL_ID,
  type EmpireId,
  type GameState,
  type SystemId,
} from '../state/types/index';
import type { GalaxyOverlay, UiState } from '../ui/state';
import { viewerEmpireId } from '../ui/state';
import { worldToScreen, type Viewport } from './camera';
import { resolvePickerSource } from './galaxy-jump-target';
import { projectGalaxyRadii } from './galaxy-lrsa-rings';

const STORAGE_KEY = 'su:galaxy-overlay';

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------

/**
 * Load the last-saved overlay choice from localStorage. Returns
 * `'none'` when nothing is stored, when storage is unavailable (SSR /
 * privacy mode), or when the stored value doesn't match the known
 * variants. Mirrors the defensive shape of `createSettingsStorage` —
 * never throws.
 */
export function loadGalaxyOverlay(): GalaxyOverlay {
  try {
    const ls = (globalThis as unknown as { localStorage?: Storage }).localStorage;
    if (!ls) return 'none';
    const raw = ls.getItem(STORAGE_KEY);
    if (raw === null) return 'none';
    if (
      raw === 'none' ||
      raw === 'production' ||
      raw === 'threat' ||
      raw === 'exploration' ||
      raw === 'diplomatic'
    ) {
      return raw;
    }
  } catch {
    // Storage threw (denied / quota / etc.) — fall back to default.
  }
  return 'none';
}

/**
 * Persist the active overlay choice. No-op on storage errors so a
 * privacy-mode browser doesn't crash on hotkey press.
 */
export function saveGalaxyOverlay(o: GalaxyOverlay): void {
  try {
    const ls = (globalThis as unknown as { localStorage?: Storage }).localStorage;
    if (!ls) return;
    ls.setItem(STORAGE_KEY, o);
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Score functions
// ---------------------------------------------------------------------------

/**
 * Per-system viewer-empire per-tick production score. Sums credits +
 * minerals + research + fuel income from each viewer-owned planet in
 * the system, applying the same economy multipliers + habitability
 * efficiency the sim uses in `advance.ts` step 3a. AI bonus and
 * vassal-tribute routing are intentionally omitted — the overlay is a
 * viewer-perspective readout, not an audit of the actual delta hitting
 * the treasury.
 *
 * Returns 0 for systems with no viewer-owned planets. Diplomatic
 * income (Embassy-driven) isn't included — it's empire-wide rather
 * than per-system, so wouldn't read meaningfully on a per-system tint.
 */
export function productionScore(
  state: GameState,
  viewerId: EmpireId,
  sysId: SystemId,
): number {
  const system = state.systems.get(sysId);
  if (!system) return 0;
  const viewer = state.empires.get(viewerId);
  if (!viewer) return 0;
  const popOut = state.balanceData.economy.popOutputPerTick;
  let total = 0;
  for (const pid of system.planetIds) {
    const planet = state.planets.get(pid);
    if (!planet || planet.ownerEmpireId !== viewerId) continue;
    const m = economyMultipliersForPlanet(state, planet.id);
    const habit = habitabilityFor(
      planet,
      viewer.raceId,
      viewer.researchedTechs,
      state.habitability,
    );
    const eff = productionEfficiencyMultiplier(habit, state.habitability);
    const pop = planet.currentPopulation;
    total +=
      pop *
      eff *
      (popOut.credits * m.credits +
        popOut.research * m.research +
        popOut.minerals * m.minerals +
        popOut.fuel * m.fuel);
  }
  return total;
}

/**
 * MFix30.2 — per-system per-resource breakdown for the Production
 * overlay's text-label render. Mirrors the `productionScore` formula
 * but splits the output by resource, plus folds in a population delta
 * (per-tick growth/decline using the same `popDeltaPerTick` + economy
 * multiplier the sim uses in `advance.ts` step 3a, Cloning Center
 * stacking on positive deltas only).
 *
 * Returns all-zero breakdown for systems with no viewer-owned planets.
 * Diplomatic income is empire-wide and intentionally omitted (same
 * reasoning as `productionScore`).
 */
export interface ProductionBreakdown {
  credits: number;
  minerals: number;
  fuel: number;
  research: number;
  /** Per-tick population delta — positive = growth, negative = decline. */
  population: number;
}

export function productionBreakdown(
  state: GameState,
  viewerId: EmpireId,
  sysId: SystemId,
): ProductionBreakdown {
  const out: ProductionBreakdown = {
    credits: 0,
    minerals: 0,
    fuel: 0,
    research: 0,
    population: 0,
  };
  const system = state.systems.get(sysId);
  if (!system) return out;
  const viewer = state.empires.get(viewerId);
  if (!viewer) return out;
  const popOut = state.balanceData.economy.popOutputPerTick;
  for (const pid of system.planetIds) {
    const planet = state.planets.get(pid);
    if (!planet || planet.ownerEmpireId !== viewerId) continue;
    const m = economyMultipliersForPlanet(state, planet.id);
    const habit = habitabilityFor(
      planet,
      viewer.raceId,
      viewer.researchedTechs,
      state.habitability,
    );
    const eff = productionEfficiencyMultiplier(habit, state.habitability);
    const pop = planet.currentPopulation;
    out.credits += pop * eff * popOut.credits * m.credits;
    out.research += pop * eff * popOut.research * m.research;
    out.minerals += pop * eff * popOut.minerals * m.minerals;
    out.fuel += pop * eff * popOut.fuel * m.fuel;
    // Population delta — mirrors advance.ts step 3a: Cloning Center
    // stacks on positive deltas only; hostile decline is not boosted.
    const baseDelta = popDeltaPerTick(
      habit,
      planet.currentPopulation,
      planet.populationCap,
      state.habitability,
    );
    out.population += baseDelta >= 0 ? baseDelta * m.popGrowth : baseDelta;
  }
  return out;
}

/**
 * Per-system threat score from the viewer-empire's perspective. Reuses
 * `enemyStrength` (v4M8.2a, AI.md §5.1) which already team-vision-gates
 * via `state.teamVision` — so the overlay never reveals enemies hidden
 * in unscouted systems. Returns 0 when the viewer empire isn't found.
 */
export function threatScore(
  state: GameState,
  viewerId: EmpireId,
  sysId: SystemId,
): number {
  const viewer = state.empires.get(viewerId);
  if (!viewer) return 0;
  return enemyStrength(state, viewer, sysId);
}

/**
 * Per-system exploration state — 1 if scouted, 0 if not. Reads
 * `Empire.scoutedSystemIds` directly. Returns 0 when the viewer empire
 * isn't found.
 */
export function explorationScore(
  state: GameState,
  viewerId: EmpireId,
  sysId: SystemId,
): 0 | 1 {
  const viewer = state.empires.get(viewerId);
  if (!viewer) return 0;
  return viewer.scoutedSystemIds.has(sysId) ? 1 : 0;
}

/**
 * Per-system dominant diplomatic state from the viewer-empire's
 * perspective. Classifies each planet (own / federated / vassal-host
 * / trading / courting / at-war / neutral) and picks the highest-
 * priority state present — own beats federated beats vassal-host etc.
 *
 * Non-owned-by-viewer planets in unscouted systems are skipped (FoW
 * consistency: the overlay shouldn't leak diplomatic status for a
 * system you haven't visited). Own planets are always visible.
 * Returns `'unknown'` when no planets contribute.
 */
export type DiplomaticOverlayState =
  | 'own'
  | 'federated'
  | 'vassal-host'
  | 'trading'
  | 'courting'
  | 'at-war'
  | 'neutral'
  | 'unknown';

/** Priority order (highest first) for dominant-state resolution. */
const DIPLOMATIC_PRIORITY: readonly DiplomaticOverlayState[] = [
  'own',
  'federated',
  'vassal-host',
  'trading',
  'courting',
  'at-war',
  'neutral',
];

export function diplomaticScore(
  state: GameState,
  viewerId: EmpireId,
  sysId: SystemId,
): DiplomaticOverlayState {
  const system = state.systems.get(sysId);
  if (!system) return 'unknown';
  const viewer = state.empires.get(viewerId);
  if (!viewer) return 'unknown';
  const scouted = viewer.scoutedSystemIds.has(sysId);
  const present = new Set<DiplomaticOverlayState>();
  for (const pid of system.planetIds) {
    const planet = state.planets.get(pid);
    if (!planet) continue;
    // Own planets always visible.
    if (planet.ownerEmpireId === viewerId) {
      present.add('own');
      continue;
    }
    // FoW gate for everything else — same rule the legacy renderer
    // applies for planet dots / labels.
    if (!scouted) continue;
    if (planet.ownerEmpireId === EMPIRE_NEUTRAL_ID) {
      if (planet.federatedWith === viewerId) {
        present.add('federated');
        continue;
      }
      if (planet.vassalOf === viewerId) {
        present.add('vassal-host');
        continue;
      }
      if (planet.tradingWith.includes(viewerId)) {
        present.add('trading');
        continue;
      }
      if (planet.influence.has(viewerId) && (planet.influence.get(viewerId) ?? 0) > 0) {
        present.add('courting');
        continue;
      }
      present.add('neutral');
      continue;
    }
    // Owned by another empire: at-war unless teammate (teammates aren't
    // a distinct overlay colour in v4M6.2 — bundled under 'own' would
    // mislead; bundled under 'neutral' is the lesser-evil placeholder
    // until a 'team' state lands separately).
    //
    // Defensive null guard: in practice every planet either belongs to
    // an empire or to `EMPIRE_NEUTRAL_ID` (per v3M21.1 seeding), so the
    // null branch is unreachable from runtime state. The check is here
    // to satisfy TS strict-null narrowing — `Planet.ownerEmpireId`
    // types as `EmpireId | null` and the earlier `=== EMPIRE_NEUTRAL_ID`
    // check doesn't eliminate the null arm of the union.
    if (planet.ownerEmpireId === null) continue;
    if (areTeammates(state, viewerId, planet.ownerEmpireId)) {
      present.add('own');
      continue;
    }
    present.add('at-war');
  }
  for (const s of DIPLOMATIC_PRIORITY) {
    if (present.has(s)) return s;
  }
  return 'unknown';
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

/** Colour palette for the diplomatic-state overlay. */
const DIPLOMATIC_COLORS: Record<DiplomaticOverlayState, string> = {
  own: '#7acbff',
  federated: '#a070ff',
  'vassal-host': '#c084ff',
  trading: '#ffd27a',
  courting: '#ffae6e',
  'at-war': '#ff6868',
  neutral: '#9fb3d9',
  unknown: '#3a4a66',
};

/** Threat ramp — neutral grey through orange to fierce red. */
function heatRampThreat(t: number): string {
  const x = Math.max(0, Math.min(1, t));
  if (x < 0.5) {
    const k = x / 0.5;
    return rgba(120 + 100 * k, 130 - 30 * k, 130 - 50 * k, 0.45 + 0.15 * k);
  }
  const k = (x - 0.5) / 0.5;
  return rgba(220 + 30 * k, 100 - 80 * k, 80 - 60 * k, 0.6 + 0.2 * k);
}

function rgba(r: number, g: number, b: number, a: number): string {
  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a.toFixed(3)})`;
}

/**
 * MFix30.2 — format a per-tick production value for the per-system
 * text panel. Values < 10 get one decimal place, larger values round
 * to whole numbers; signed prefix always present so the player can
 * read positive vs negative deltas at a glance (the population row
 * is the only one that goes negative, but the consistent format
 * helps the column align).
 */
function fmtProduction(value: number): string {
  const sign = value > 0 ? '+' : value < 0 ? '−' : '±';
  const abs = Math.abs(value);
  if (abs < 10) return `${sign}${abs.toFixed(1)}`;
  return `${sign}${Math.round(abs)}`;
}

/**
 * Draw the active overlay across every system. No-op when
 * `ui.galaxyOverlay === 'none'` or the view mode isn't 'galaxy'.
 * Caller is `galaxy-view.ts`, which inserts this between the star
 * halo block and the per-system indicator stack.
 *
 * `starRadius` is the same base radius galaxy-view computes — used to
 * size the overlay ring proportionally to the star sprite so the tint
 * tracks zoom without overrunning the indicator column.
 */
export function drawGalaxyOverlay(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  vp: Viewport,
  starRadius: number,
): void {
  if (ui.galaxyOverlay === 'none') return;
  const viewerId = viewerEmpireId(state, ui);

  // MFix30.2 — Production overlay switches from heat-map ring to a
  // per-system per-resource text panel. Each viewer-inhabited system
  // shows five numbers (C/M/F/R/P) to the LEFT of the star so the
  // existing indicator stack on the right stays untouched. Systems
  // without viewer-owned planets get no panel.
  if (ui.galaxyOverlay === 'production') {
    ctx.save();
    ctx.font = '11px Inter, system-ui, "Segoe UI", sans-serif';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'right';
    for (const sys of state.systems.values()) {
      const bd = productionBreakdown(state, viewerId, sys.id);
      // Skip systems with no viewer-owned production. Population delta
      // can be slightly negative on a hostile-band planet even with zero
      // resource generation, so check resources OR a meaningful pop
      // delta rather than just `total > 0`.
      if (
        bd.credits === 0 &&
        bd.minerals === 0 &&
        bd.fuel === 0 &&
        bd.research === 0 &&
        Math.abs(bd.population) < 0.001
      ) {
        continue;
      }
      const p = worldToScreen(ui.galaxyCamera, vp, { x: sys.x, y: sys.y });
      // Anchor: just outside the per-system halo band, opposite the
      // right-side indicator stack. Five lines stacked vertically.
      const anchorX = p.x - starRadius - 14;
      const lineHeight = 13;
      const lines: Array<[string, number, string]> = [
        ['C', bd.credits, '#ffd27a'],
        ['M', bd.minerals, '#b89878'],
        ['F', bd.fuel, '#ffae6e'],
        ['R', bd.research, '#9fc8ff'],
        ['P', bd.population, '#a8e8a8'],
      ];
      const blockH = lineHeight * lines.length;
      let y = p.y - blockH / 2 + lineHeight / 2;
      for (const [label, value, color] of lines) {
        ctx.fillStyle = color;
        ctx.fillText(`${label} ${fmtProduction(value)}`, anchorX, y);
        y += lineHeight;
      }
    }
    ctx.restore();
    // Legend retained — heat-map gradient is irrelevant for the text
    // mode but the per-letter colour key helps the player decode the
    // five rows at a glance.
    drawOverlayLegend(ctx, ui, vp, 0);
    return;
  }

  if (ui.galaxyOverlay === 'threat') {
    const scores = new Map<SystemId, number>();
    let maxScore = 0;
    for (const sys of state.systems.values()) {
      const s = threatScore(state, viewerId, sys.id);
      scores.set(sys.id, s);
      if (s > maxScore) maxScore = s;
    }
    ctx.save();
    for (const sys of state.systems.values()) {
      const s = scores.get(sys.id) ?? 0;
      if (s <= 0) continue;
      const norm = maxScore > 0 ? s / maxScore : 0;
      const p = worldToScreen(ui.galaxyCamera, vp, { x: sys.x, y: sys.y });
      ctx.strokeStyle = heatRampThreat(norm);
      ctx.lineWidth = Math.max(3, starRadius * 0.35);
      ctx.beginPath();
      ctx.arc(p.x, p.y, starRadius * 1.8, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
    drawOverlayLegend(ctx, ui, vp, maxScore);
    return;
  }

  if (ui.galaxyOverlay === 'exploration') {
    ctx.save();
    for (const sys of state.systems.values()) {
      const v = explorationScore(state, viewerId, sys.id);
      const p = worldToScreen(ui.galaxyCamera, vp, { x: sys.x, y: sys.y });
      // Scouted = soft warm wash; unscouted = a darker cool ring so
      // the unexplored systems read as "there's something there but
      // you don't know what."
      ctx.strokeStyle = v === 1 ? 'rgba(140, 220, 160, 0.55)' : 'rgba(60, 90, 130, 0.55)';
      ctx.lineWidth = Math.max(3, starRadius * 0.35);
      ctx.beginPath();
      ctx.arc(p.x, p.y, starRadius * 1.8, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
    // MFix39 (2026-05-30) — hyperdrive range ring around the selected
    // fleet's source system, shown only on the Exploration overlay.
    // Helps the player see at a glance "from this system, where can
    // my selected fleet actually reach." Uses the same source-resolution
    // logic as the v4M6.3a destination picker (`resolvePickerSource`)
    // so the source-system + effective range derivation stays in
    // lockstep — selection that spans multiple source systems or
    // includes non-player ships yields no ring. Ellipse projection via
    // `projectGalaxyRadii` (MFix37) so the ring matches the actual
    // reach disk on non-square viewports.
    drawExplorationRangeRing(ctx, state, ui, vp);
    drawOverlayLegend(ctx, ui, vp, 0);
    return;
  }

  if (ui.galaxyOverlay === 'diplomatic') {
    ctx.save();
    for (const sys of state.systems.values()) {
      const v = diplomaticScore(state, viewerId, sys.id);
      const p = worldToScreen(ui.galaxyCamera, vp, { x: sys.x, y: sys.y });
      ctx.strokeStyle = DIPLOMATIC_COLORS[v];
      ctx.lineWidth = Math.max(3, starRadius * 0.35);
      ctx.beginPath();
      ctx.arc(p.x, p.y, starRadius * 1.8, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.restore();
    drawOverlayLegend(ctx, ui, vp, 0);
    return;
  }
}

/**
 * MFix39 — hyperdrive range ring for the Exploration overlay. Reads
 * the selected fleet's source-system + effective range via
 * `resolvePickerSource` (galaxy-jump-target.ts), projects the
 * galaxy-frame radius into screen-space semi-axes via
 * `projectGalaxyRadii` (galaxy-lrsa-rings.ts; MFix37), and strokes
 * an empire-coloured dashed ellipse. Distinct from the LRSA ring
 * (different dash pattern + slightly higher alpha) so the two reads
 * don't conflict when both are visible.
 *
 * No-op when:
 *   - no fleet selected
 *   - selection spans multiple source systems
 *   - selection includes non-player-owned ships
 *   - all selected ships are mid-jump
 *   - the computed reach radius collapses to sub-pixel at current zoom.
 */
function drawExplorationRangeRing(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  ui: UiState,
  vp: Viewport,
): void {
  const source = resolvePickerSource(state, ui);
  if (source === null) return;
  const sys = state.systems.get(source.systemId);
  if (!sys) return;
  const center = worldToScreen(ui.galaxyCamera, vp, { x: sys.x, y: sys.y });
  const { rx, ry } = projectGalaxyRadii(ui.galaxyCamera, vp, source.range);
  if (Math.max(rx, ry) < 4) return; // sub-pixel cull
  ctx.save();
  ctx.strokeStyle = source.empire.color;
  ctx.lineWidth = 1.5;
  ctx.globalAlpha = 0.55;
  // Longer dash pattern distinguishes the hyperdrive ring from the
  // LRSA ring (`[6, 4]` per MFix37) so the two visual layers stay
  // independently readable when an empire has both rings on the same
  // source system.
  ctx.setLineDash([10, 5]);
  ctx.beginPath();
  ctx.ellipse(center.x, center.y, rx, ry, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();
}

/**
 * Bottom-of-view legend strip showing the active overlay's name +
 * colour ramp endpoints. Drawn in the viewport's bottom-left corner so
 * it doesn't clash with the mini-galaxy (bottom-right by default) or
 * the speed widget (top-right).
 *
 * `maxScore` is the per-frame heat-map max (production / threat); the
 * legend prints it next to the high end of the ramp. Discrete overlays
 * (exploration / diplomatic) pass 0 and use a categorical legend.
 */
function drawOverlayLegend(
  ctx: CanvasRenderingContext2D,
  ui: UiState,
  vp: Viewport,
  maxScore: number,
): void {
  const padX = vp.padLeft + 16;
  const baseY = vp.padTop + vp.viewH - 16;
  const stripW = 220;
  const stripH = 8;
  const labelY = baseY - stripH - 6;

  ctx.save();
  ctx.font = '12px Inter, system-ui, "Segoe UI", sans-serif';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = 'rgba(216, 228, 255, 0.9)';

  if (ui.galaxyOverlay === 'production') {
    // MFix30.2 — Production legend is a per-letter colour key matching
    // the per-system text block. No heat-map gradient (the breakdown
    // panels carry the readout directly).
    ctx.textAlign = 'left';
    ctx.fillText('Production / tick', padX, labelY);
    const key: Array<[string, string]> = [
      ['C credits', '#ffd27a'],
      ['M minerals', '#b89878'],
      ['F fuel', '#ffae6e'],
      ['R research', '#9fc8ff'],
      ['P pop Δ', '#a8e8a8'],
    ];
    let kx = padX;
    for (const [label, color] of key) {
      ctx.fillStyle = color;
      ctx.fillText(label, kx, baseY);
      kx += ctx.measureText(label).width + 14;
    }
    ctx.restore();
    return;
  }

  if (ui.galaxyOverlay === 'threat') {
    // Heading.
    ctx.textAlign = 'left';
    ctx.fillText('Threat score', padX, labelY);
    // Gradient strip — sample the ramp at N steps.
    const steps = 24;
    for (let i = 0; i < steps; i++) {
      const t = i / (steps - 1);
      ctx.fillStyle = heatRampThreat(t);
      ctx.fillRect(padX + (i * stripW) / steps, baseY - stripH, stripW / steps + 1, stripH);
    }
    // Endpoint labels — `0` at left, formatted max at right.
    ctx.fillStyle = 'rgba(180, 195, 220, 0.9)';
    ctx.textAlign = 'left';
    ctx.fillText('0', padX, baseY + 14);
    ctx.textAlign = 'right';
    const fmtMax =
      maxScore <= 0
        ? '—'
        : maxScore >= 100
          ? Math.round(maxScore).toLocaleString()
          : maxScore.toFixed(1);
    ctx.fillText(fmtMax, padX + stripW, baseY + 14);
    ctx.restore();
    return;
  }

  if (ui.galaxyOverlay === 'exploration') {
    ctx.textAlign = 'left';
    ctx.fillText('Exploration', padX, labelY);
    // Two swatches: scouted, unscouted.
    const swatchW = 14;
    const swatchH = 14;
    const gap = 8;
    ctx.fillStyle = 'rgba(140, 220, 160, 0.85)';
    ctx.fillRect(padX, baseY - swatchH, swatchW, swatchH);
    ctx.fillStyle = 'rgba(180, 195, 220, 0.9)';
    ctx.fillText('scouted', padX + swatchW + 6, baseY - 3);
    const ux = padX + swatchW + 6 + ctx.measureText('scouted').width + gap + 12;
    ctx.fillStyle = 'rgba(60, 90, 130, 0.85)';
    ctx.fillRect(ux, baseY - swatchH, swatchW, swatchH);
    ctx.fillStyle = 'rgba(180, 195, 220, 0.9)';
    ctx.fillText('unscouted', ux + swatchW + 6, baseY - 3);
    ctx.restore();
    return;
  }

  if (ui.galaxyOverlay === 'diplomatic') {
    ctx.textAlign = 'left';
    ctx.fillText('Diplomatic state', padX, labelY);
    const order: DiplomaticOverlayState[] = [
      'own',
      'federated',
      'vassal-host',
      'trading',
      'courting',
      'at-war',
      'neutral',
    ];
    let x = padX;
    const swatchW = 12;
    const swatchH = 12;
    const gap = 4;
    for (const s of order) {
      ctx.fillStyle = DIPLOMATIC_COLORS[s];
      ctx.fillRect(x, baseY - swatchH, swatchW, swatchH);
      ctx.fillStyle = 'rgba(180, 195, 220, 0.9)';
      const label = s === 'vassal-host' ? 'vassal' : s;
      ctx.fillText(label, x + swatchW + 3, baseY - 2);
      const w = ctx.measureText(label).width;
      x += swatchW + 3 + w + gap;
    }
    ctx.restore();
    return;
  }

  ctx.restore();
}
